import { Button, makeStyles, Typography } from "@material-ui/core"
import { useContext, useMemo, useState } from "react"
import { LoginDialogContext } from "../../../components/LoginDialog"
import SiteContext from "../../../contexts/SiteContext"
import { TextosContext } from "../../../contexts/TextosContext"
import { formatarParaDinheiro } from "../../../services/functions"
import GanheDescontos from "./../ganheDescontos"

const useStyles = makeStyles(theme => ({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        margin: "-1.5em 0 1.5em 0",
    },
    logo: {
        width: "120px",
        maxWidth: "120px",
        backgroundColor: "white",
        borderRadius: "100%",
        border: "1px #f5f5f5 solid",
        zIndex: "1",
    },
    botoes: {
        display: "flex",
        "@media (max-width: 375px)": {
            flexDirection: "column",
            gap: "4px",
        },
    },
    botao: {
        margin: "0 5px",
        fontSize: "90%",
        flex: "1 1 0",
        whiteSpace: "nowrap",
    },
    pedidoMinimo: {
        borderBottom: "1px #555 solid",
    },
    negrito: {
        fontWeight: 600,
    },
    horarios: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        gap: "4px",
        justifyContent: "space-between",
        fontFamily: '"SulSans", Helvetica, sans-serif',
        textAlign: "center",
    },
}))

const getHorariosDoDia = (horariosAtendidos, semana) => {
    if (!horariosAtendidos) return []

    const data = new Date()
    const diaDaSemana = semana[data.getDay()]
    const diaDaSemanaAmanha = semana[(data.getDay() + 1) % 7]
    const horarios = horariosAtendidos.split("\n")

    let horariosHoje = horarios.filter(h => h.includes(diaDaSemana))
    const horariosAmanha = horarios.filter(h => h.includes(diaDaSemanaAmanha))

    // Substitui 23:59 para o fim da madrugada do outro dia, caso fique aberto até depois de 23:59
    horariosAmanha.forEach(h => {
        if (h?.split(" ")[1] === "00:00" && h?.split(" ")[3] !== "23:59") {
            horariosHoje = horariosHoje.map(horarioHoje =>
                horarioHoje.split(" ")[3] === "23:59"
                    ? horarioHoje.replace("23:59", h.split(" ")[3])
                    : horarioHoje
            )
        }
    })

    return (
        horariosHoje
            // Apaga os que iniciam em 00:00
            .filter(
                horario =>
                    !horario.includes("00:00") ||
                    (!horario.includes("23:59") &&
                        60 * data.getHours() + data.getMinutes() <
                            60 * Number(horario.split(" ")[3].split(":")[0]) +
                                Number(horario.split(" ")[3].split(":")[1]))
            )
            // Retira o nome do dia da semana
            .map(horario => horario.split(" ").slice(1).join(" ").replace("23:59", "00:00"))
            .sort()
    )
}

const HorarioFuncionamento = ({ horariosAtendidos }) => {
    const { textos } = useContext(TextosContext)
    const classes = useStyles()

    const horarioDeHoje = useMemo(
        () => getHorariosDoDia(horariosAtendidos, textos.diasSemana),
        [horariosAtendidos]
    )

    if (!horarioDeHoje?.length) return null

    return (
        <div className={classes.horarios}>
            <span>
                {horarioDeHoje.length === 1
                    ? textos.horarioFuncionamento
                    : textos.horariosFuncionamento}
                :
            </span>
            {horarioDeHoje.map(horario => (
                <span className={classes.negrito}>{horario}</span>
            ))}
        </div>
    )
}

function DescricaoRestaurante({ usuarioPedidoMesa }) {
    const { textos } = useContext(TextosContext)

    const { estabelecimentoAtual } = useContext(SiteContext)

    const categoriaDoRestaurante = estabelecimentoAtual.categoria

    const tempoDeEntrega =
        !usuarioPedidoMesa?.logado && (estabelecimentoAtual.tempoEntregaTexto ?? false)

    const statusEstabelecimento = estabelecimentoAtual?.online
        ? estabelecimentoAtual.pausado
            ? textos.pausado
            : textos.aberto
        : textos.fechado

    let textoDescricao = ""
    if (!!categoriaDoRestaurante) textoDescricao = textoDescricao.concat(categoriaDoRestaurante)
    if (!!categoriaDoRestaurante && (!!tempoDeEntrega || !!statusEstabelecimento))
        textoDescricao = textoDescricao.concat(" • ")
    if (!!tempoDeEntrega) textoDescricao = textoDescricao.concat(tempoDeEntrega)
    if (!!tempoDeEntrega && !!statusEstabelecimento) textoDescricao = textoDescricao.concat(" • ")
    if (!!statusEstabelecimento) textoDescricao = textoDescricao.concat(statusEstabelecimento)

    return <span>{textoDescricao}</span>
}

function PedidoMinimo() {
    const { textos } = useContext(TextosContext)

    const { cardapio, enderecoAtual } = useContext(SiteContext)

    const isRetiradaNoLocal =
        enderecoAtual?.id === "retirada" || enderecoAtual.logradouro === "Buscar no local"
    const isRetiradaValorMinimo = cardapio?.valorMinimoRetiradaNoLocal > 0 && isRetiradaNoLocal

    const classes = useStyles()

    if (
        !cardapio ||
        (!(cardapio.minimoEntregaGratis && cardapio.minimoEntregaGratis < 9000) &&
            !cardapio.valorMinimoPedido)
    )
        return null

    return (
        <Typography gutterBottom variant="subtitle2" className={classes.pedidoMinimo}>
            {cardapio.minimoEntregaGratis && cardapio.minimoEntregaGratis < 9000
                ? `${textos.minimoEntregaGratis}: ${formatarParaDinheiro(
                      cardapio.minimoEntregaGratis
                  )}`
                : isRetiradaValorMinimo
                ? `${textos.pedidoMinimoRetirada}: ${formatarParaDinheiro(
                      cardapio.valorMinimoRetiradaNoLocal
                  )}`
                : cardapio.valorMinimoPedido
                ? `${textos.pedidoMinimo}: ${formatarParaDinheiro(cardapio.valorMinimoPedido)}`
                : ""}
        </Typography>
    )
}

const BotaoDesconto = ({ temDesconto, handleGanheDescontos, isUsuarioPedidoMesa }) => {
    const classes = useStyles()

    const { textos } = useContext(TextosContext)
    const { cardapio, aplicativoDados } = useContext(SiteContext)

    const exibirGanheDescontos = !isUsuarioPedidoMesa && aplicativoDados.exibirFormasGanharDesconto

    if (!temDesconto && !exibirGanheDescontos) return null

    const textoBotao = temDesconto
        ? cardapio.valorDesconto
            ? `${textos.desconto}: ${formatarParaDinheiro(cardapio.valorDesconto)}`
            : cardapio.percentualDesconto
            ? `${textos.desconto}: ${cardapio.percentualDesconto}%`
            : textos.freteGratis
        : exibirGanheDescontos
        ? textos.ganheDescontos
        : ""

    return (
        <Button variant="outlined" className={classes.botao} onClick={handleGanheDescontos}>
            {textoBotao}
        </Button>
    )
}

export default function InfoCardapio(props) {
    const { textos } = useContext(TextosContext)
    const { setAbrirDialogLogin } = useContext(LoginDialogContext)

    const [openGanheDescontos, setOpenGanheDescontos] = useState(false)

    const classes = useStyles()

    const {
        isEntregaNoEndereco,
        isRetiradaNoLocal,
        estabelecimentoAtual,
        enderecoAtual,
        mostrarWhatsapp,
        opcoesParaEntregaeRetirada,
        temEndereco,
        cardapio,
        setRodarFuncaoAposIsso,
        aplicativoDados,
        history,
        usuario = {},
        visualizacao,
        taxa,
        usuarioPedidoMesa,
        isUsuarioPedidoMesa = usuarioPedidoMesa?.logado,
    } = props

    const handleGanheDescontos = () => {
        if (aplicativoDados.exibirFormasGanharDesconto) setOpenGanheDescontos(!openGanheDescontos)
    }

    const aberto = estabelecimentoAtual?.online

    const temDesconto =
        cardapio.valorDesconto ||
        cardapio.percentualDesconto ||
        cardapio?.cupomDesconto?.freteGratis

    return (
        <div className={classes.container}>
            <img src={estabelecimentoAtual.urlLogo} className={classes.logo} />
            <DescricaoRestaurante usuarioPedidoMesa={usuarioPedidoMesa} />
            <HorarioFuncionamento horariosAtendidos={estabelecimentoAtual?.horariosAtendidos} />
            <PedidoMinimo />
            {!(visualizacao || aplicativoDados.clienteFielStart) ? (
                <div className={classes.botoes}>
                    {!isUsuarioPedidoMesa ? (
                        temEndereco ? (
                            <Button
                                variant="outlined"
                                className={classes.botao}
                                onClick={opcoesParaEntregaeRetirada}
                            >
                                {isEntregaNoEndereco && taxa >= 0
                                    ? `${textos.entrega}: ${
                                          taxa === 0 ? textos.gratis : formatarParaDinheiro(taxa)
                                      }`
                                    : textos.naoEntrega}
                            </Button>
                        ) : usuario?.logado ? (
                            <Button
                                variant="outlined"
                                className={classes.botao}
                                onClick={opcoesParaEntregaeRetirada}
                            >
                                {isRetiradaNoLocal
                                    ? textos.retiradaNoLocal
                                    : textos.adicioneEndereco}
                            </Button>
                        ) : temDesconto ||
                          (!isUsuarioPedidoMesa && aplicativoDados.exibirFormasGanharDesconto) ? (
                            <Button
                                variant="outlined"
                                className={classes.botao}
                                onClick={() => setAbrirDialogLogin(true)}
                            >
                                {textos.facaLogin}
                            </Button>
                        ) : null
                    ) : null}

                    <BotaoDesconto
                        handleGanheDescontos={handleGanheDescontos}
                        temDesconto={temDesconto}
                        isUsuarioPedidoMesa={isUsuarioPedidoMesa}
                    />
                </div>
            ) : null}

            {openGanheDescontos && (
                <GanheDescontos
                    setOpenGanheDescontos={setOpenGanheDescontos}
                    openGanheDescontos={openGanheDescontos}
                    usuario={usuario}
                    temDesconto={temDesconto}
                    cardapio={cardapio}
                    aplicativoDados={aplicativoDados}
                    estabelecimentoAtual={estabelecimentoAtual}
                />
            )}
        </div>
    )
}
